import { cn } from '@plato/ui/util/classes';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithRef, ReactElement } from 'react';

export type MessageProps = ComponentPropsWithRef<'div'> & {
    align?: 'start' | 'end';
};

export function Message({ className, align = 'start', ...props }: MessageProps): ReactElement {
    return (
        <div
            data-slot="message"
            data-align={align}
            className={cn(
                'group/message flex w-full min-w-0 items-end gap-cozy',
                'data-[align=end]:flex-row-reverse',
                className,
            )}
            {...props}
        />
    );
}

const messageContentVariants = cva(
    [
        'flex min-w-0 flex-col gap-cozy',
        'text-ui-base text-content whitespace-pre-wrap wrap-break-word',
        'group-data-[align=end]/message:self-end',
    ],
    {
        variants: {
            variant: {
                bubble: 'w-fit max-w-4/5 rounded-relaxed bg-container-inset px-default py-cozy',
                tinted: 'w-fit max-w-4/5 rounded-relaxed bg-brand/(--opacity-wash) px-default py-cozy',
                ghost: 'w-full',
            },
        },
        defaultVariants: {
            variant: 'bubble',
        },
    },
);

export type MessageContentVariant = NonNullable<VariantProps<typeof messageContentVariants>['variant']>;

export type MessageContentProps = ComponentPropsWithRef<'div'> & VariantProps<typeof messageContentVariants>;

export function MessageContent({ className, variant, ...props }: MessageContentProps): ReactElement {
    return (
        <div
            data-slot="message-content"
            data-variant={variant ?? 'bubble'}
            className={cn(messageContentVariants({ variant }), className)}
            {...props}
        />
    );
}
